import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Accordion,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';
import {
  THICKNESS_OPTIONS,
  DRAINAGE_TYPES,
  ODOR_TYPES,
} from '../../constants/wounds';
import type {
  WoundAssessmentCreate,
  WoundAssessmentResponse,
} from '../../services/api';
import {
  DEFAULT_DRAINAGE,
  DEFAULT_EDGES,
  DEFAULT_NOTES,
  DEFAULT_ODOR,
  DEFAULT_PAIN,
  DEFAULT_SIZE,
  DEFAULT_SURROUNDING_TISSUE,
  DEFAULT_THICKNESS,
  DEFAULT_TUNNELING,
  DEFAULT_UNDERMINING,
  DEFAULT_WOUND_BED,
  type DrainageState,
  type EdgesState,
  type NotesState,
  type OdorState,
  type PainState,
  type SizeState,
  type SurroundingTissueState,
  type ThicknessState,
  type TunnelingState,
  type UnderminingRow,
  type UnderminingState,
  type WoundBedState,
} from './assessmentFormTypes';
import OverviewSection from './sections/OverviewSection';
import SizeSection from './sections/SizeSection';
import ThicknessSection from './sections/ThicknessSection';
import EdgesSection from './sections/EdgesSection';
import DrainageSection from './sections/DrainageSection';
import OdorSection from './sections/OdorSection';
import WoundBedSection from './sections/WoundBedSection';
import SurroundingTissueSection from './sections/SurroundingTissueSection';
import UnderminingSection from './sections/UnderminingSection';
import TunnelingSection from './sections/TunnelingSection';
import PainSection from './sections/PainSection';
import NotesSection from './sections/NotesSection';

interface WoundAssessmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  woundId: string;
  visitId?: string;
  editingAssessment: WoundAssessmentResponse | null;
  previousAssessment?: WoundAssessmentResponse | null;
  /** Why the last save was refused. Shown here because the drawer's own error
      area sits behind this modal's overlay. */
  saveError?: string | null;
  onSave: (data: WoundAssessmentCreate) => Promise<void>;
}

const KNOWN_THICKNESS_VALUES = new Set(
  THICKNESS_OPTIONS.filter(o => o.value !== 'other').map(o => o.value)
);
const KNOWN_DRAINAGE_TYPE_VALUES = new Set(
  DRAINAGE_TYPES.filter(o => o.value !== 'other').map(o => o.value)
);
const KNOWN_ODOR_TYPE_VALUES = new Set(
  ODOR_TYPES.filter(o => o.value !== 'other').map(o => o.value)
);

const DEFAULT_SECTIONS = [
  'overview',
  'size',
  'thickness',
  'edges',
  'drainage',
  'odor',
  'wound_bed',
  'surrounding_tissue',
  'undermining',
  'tunneling',
  'pain',
  'notes',
];

const makePartialUpdater =
  <T,>(setter: React.Dispatch<React.SetStateAction<T>>) =>
  (update: Partial<T>) =>
    setter(prev => ({ ...prev, ...update }));

const WoundAssessmentDialog: React.FC<WoundAssessmentDialogProps> = ({
  open,
  onOpenChange,
  woundId,
  visitId,
  editingAssessment,
  previousAssessment,
  saveError,
  onSave,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [isAddressed, setIsAddressed] = useState(true);
  const [sizeState, setSizeState] = useState<SizeState>(DEFAULT_SIZE);
  const [thicknessState, setThicknessState] =
    useState<ThicknessState>(DEFAULT_THICKNESS);
  const [edgesState, setEdgesState] = useState<EdgesState>(DEFAULT_EDGES);
  const [drainageState, setDrainageState] =
    useState<DrainageState>(DEFAULT_DRAINAGE);
  const [odorState, setOdorState] = useState<OdorState>(DEFAULT_ODOR);
  const [woundBedState, setWoundBedState] =
    useState<WoundBedState>(DEFAULT_WOUND_BED);
  const [surroundingTissueState, setSurroundingTissueState] =
    useState<SurroundingTissueState>(DEFAULT_SURROUNDING_TISSUE);
  const [underminingState, setUnderminingState] =
    useState<UnderminingState>(DEFAULT_UNDERMINING);
  const [tunnelingState, setTunnelingState] =
    useState<TunnelingState>(DEFAULT_TUNNELING);
  const [painState, setPainState] = useState<PainState>(DEFAULT_PAIN);
  const [notesState, setNotesState] = useState<NotesState>(DEFAULT_NOTES);

  useEffect(() => {
    if (!open) return;
    const source = editingAssessment ?? previousAssessment ?? null;
    if (!source) {
      setIsAddressed(true);
      setSizeState(DEFAULT_SIZE);
      setThicknessState(DEFAULT_THICKNESS);
      setEdgesState(DEFAULT_EDGES);
      setDrainageState(DEFAULT_DRAINAGE);
      setOdorState(DEFAULT_ODOR);
      setWoundBedState(DEFAULT_WOUND_BED);
      setSurroundingTissueState(DEFAULT_SURROUNDING_TISSUE);
      setUnderminingState(DEFAULT_UNDERMINING);
      setTunnelingState(DEFAULT_TUNNELING);
      setPainState(DEFAULT_PAIN);
      setNotesState(DEFAULT_NOTES);
      return;
    }

    const a = source;
    const reasons = (a.not_assessed_reasons as Record<string, string>) ?? {};
    const storedThickness = a.thickness ?? '';
    const storedDrainageType = a.drainage_type ?? '';
    const storedOdorType = a.odor_type ?? '';

    setIsAddressed(a.is_addressed);
    setSizeState({
      na: a.size_na,
      length: a.length_cm != null ? String(a.length_cm) : '',
      width: a.width_cm != null ? String(a.width_cm) : '',
      depth: a.depth_cm != null ? String(a.depth_cm) : '',
    });
    setThicknessState({
      value:
        !storedThickness || KNOWN_THICKNESS_VALUES.has(storedThickness)
          ? storedThickness
          : 'other',
      other:
        !storedThickness || KNOWN_THICKNESS_VALUES.has(storedThickness)
          ? ''
          : storedThickness,
      notAssessedReason: reasons['thickness'] ?? '',
    });
    setEdgesState({
      notAssessed: a.edges_not_assessed,
      notAssessedReason: reasons['edges'] ?? '',
      values: a.edges ?? [],
    });
    setDrainageState({
      value: a.drainage ?? '',
      type:
        !storedDrainageType ||
        KNOWN_DRAINAGE_TYPE_VALUES.has(storedDrainageType)
          ? storedDrainageType
          : 'other',
      typeOther:
        !storedDrainageType ||
        KNOWN_DRAINAGE_TYPE_VALUES.has(storedDrainageType)
          ? ''
          : storedDrainageType,
      amount: a.drainage_amount ?? '',
      notAssessedReason: reasons['drainage'] ?? '',
    });
    setOdorState({
      value: a.odor ?? '',
      type:
        !storedOdorType || KNOWN_ODOR_TYPE_VALUES.has(storedOdorType)
          ? storedOdorType
          : 'other',
      typeOther:
        !storedOdorType || KNOWN_ODOR_TYPE_VALUES.has(storedOdorType)
          ? ''
          : storedOdorType,
      notAssessedReason: reasons['odor'] ?? '',
    });
    setWoundBedState({
      notAssessed: a.wound_bed_not_assessed,
      notAssessedReason: reasons['wound_bed'] ?? '',
      pcts: {
        wound_bed_pink_red_pct:
          a.wound_bed_pink_red_pct != null
            ? String(a.wound_bed_pink_red_pct)
            : '',
        wound_bed_granulation_pct:
          a.wound_bed_granulation_pct != null
            ? String(a.wound_bed_granulation_pct)
            : '',
        wound_bed_slough_pct:
          a.wound_bed_slough_pct != null ? String(a.wound_bed_slough_pct) : '',
        wound_bed_eschar_pct:
          a.wound_bed_eschar_pct != null ? String(a.wound_bed_eschar_pct) : '',
        wound_bed_necrotic_pct:
          a.wound_bed_necrotic_pct != null
            ? String(a.wound_bed_necrotic_pct)
            : '',
      },
      other: !!a.wound_bed_other_text,
      otherText: a.wound_bed_other_text ?? '',
    });
    setSurroundingTissueState({
      notAssessed: a.surrounding_tissue_not_assessed,
      notAssessedReason: reasons['surrounding_tissue'] ?? '',
      values: a.surrounding_tissue ?? [],
      otherText: a.surrounding_tissue_other_text ?? '',
    });
    setUnderminingState({
      value: a.undermining ?? '',
      notAssessedReason: reasons['undermining'] ?? '',
      rows: (a.undermining_entries as UnderminingRow[] | null) ?? [],
    });
    setTunnelingState({
      value: a.tunneling ?? '',
      notAssessedReason: reasons['tunneling'] ?? '',
      direction:
        a.tunneling_direction != null ? String(a.tunneling_direction) : '',
      depth: a.tunneling_depth_cm != null ? String(a.tunneling_depth_cm) : '',
    });
    setPainState({
      present: a.pain ?? false,
      level: a.pain_level != null ? String(a.pain_level) : '',
    });
    setNotesState({
      woundCareNotes: a.wound_care_notes ?? '',
      treatmentPerformed: a.treatment_performed ?? '',
      patientResponse: a.patient_response ?? '',
      additionalInfo: a.additional_info ?? '',
    });
  }, [open, editingAssessment, previousAssessment]);

  const parseNum = (s: string): number | null => {
    const n = parseFloat(s);
    return isNaN(n) ? null : n;
  };

  const parseIntNum = (s: string): number | null => {
    const n = parseInt(s, 10);
    return isNaN(n) ? null : n;
  };

  const handleSave = async () => {
    const notAssessedReasons: Record<string, string> = {};
    if (
      thicknessState.value === 'unable_to_determine' &&
      thicknessState.notAssessedReason
    )
      notAssessedReasons['thickness'] = thicknessState.notAssessedReason;
    if (edgesState.notAssessed && edgesState.notAssessedReason)
      notAssessedReasons['edges'] = edgesState.notAssessedReason;
    if (
      drainageState.value === 'not_assessed' &&
      drainageState.notAssessedReason
    )
      notAssessedReasons['drainage'] = drainageState.notAssessedReason;
    if (odorState.value === 'not_assessed' && odorState.notAssessedReason)
      notAssessedReasons['odor'] = odorState.notAssessedReason;
    if (woundBedState.notAssessed && woundBedState.notAssessedReason)
      notAssessedReasons['wound_bed'] = woundBedState.notAssessedReason;
    if (
      surroundingTissueState.notAssessed &&
      surroundingTissueState.notAssessedReason
    )
      notAssessedReasons['surrounding_tissue'] =
        surroundingTissueState.notAssessedReason;
    if (
      underminingState.value === 'not_assessed' &&
      underminingState.notAssessedReason
    )
      notAssessedReasons['undermining'] = underminingState.notAssessedReason;
    if (
      tunnelingState.value === 'not_assessed' &&
      tunnelingState.notAssessedReason
    )
      notAssessedReasons['tunneling'] = tunnelingState.notAssessedReason;

    setIsSaving(true);
    try {
      await onSave({
        wound_id: woundId,
        visit_id: editingAssessment ? editingAssessment.visit_id : visitId!,
        is_addressed: isAddressed,
        size_na: sizeState.na,
        length_cm: sizeState.na ? null : parseNum(sizeState.length),
        width_cm: sizeState.na ? null : parseNum(sizeState.width),
        depth_cm: sizeState.na ? null : parseNum(sizeState.depth),
        thickness:
          thicknessState.value === 'other'
            ? thicknessState.other.trim() || null
            : thicknessState.value || null,
        edges_not_assessed: edgesState.notAssessed,
        edges: edgesState.notAssessed
          ? null
          : edgesState.values.length > 0
            ? edgesState.values
            : null,
        drainage: drainageState.value || null,
        drainage_type:
          drainageState.value === 'yes'
            ? drainageState.type === 'other'
              ? drainageState.typeOther.trim() || null
              : drainageState.type || null
            : null,
        drainage_amount:
          drainageState.value === 'yes' ? drainageState.amount || null : null,
        odor: odorState.value || null,
        odor_type:
          odorState.value === 'yes'
            ? odorState.type === 'other'
              ? odorState.typeOther.trim() || null
              : odorState.type || null
            : null,
        wound_bed_not_assessed: woundBedState.notAssessed,
        wound_bed_pink_red_pct: woundBedState.notAssessed
          ? null
          : parseNum(woundBedState.pcts.wound_bed_pink_red_pct),
        wound_bed_granulation_pct: woundBedState.notAssessed
          ? null
          : parseNum(woundBedState.pcts.wound_bed_granulation_pct),
        wound_bed_slough_pct: woundBedState.notAssessed
          ? null
          : parseNum(woundBedState.pcts.wound_bed_slough_pct),
        wound_bed_eschar_pct: woundBedState.notAssessed
          ? null
          : parseNum(woundBedState.pcts.wound_bed_eschar_pct),
        wound_bed_necrotic_pct: woundBedState.notAssessed
          ? null
          : parseNum(woundBedState.pcts.wound_bed_necrotic_pct),
        wound_bed_other_text:
          woundBedState.notAssessed || !woundBedState.other
            ? null
            : woundBedState.otherText.trim() || null,
        surrounding_tissue_not_assessed: surroundingTissueState.notAssessed,
        surrounding_tissue: surroundingTissueState.notAssessed
          ? null
          : surroundingTissueState.values.length > 0
            ? surroundingTissueState.values
            : null,
        surrounding_tissue_other_text:
          surroundingTissueState.notAssessed ||
          !surroundingTissueState.values.includes('other')
            ? null
            : surroundingTissueState.otherText.trim() || null,
        undermining: underminingState.value || null,
        undermining_entries:
          underminingState.value === 'yes' ? underminingState.rows : null,
        tunneling: tunnelingState.value || null,
        tunneling_direction:
          tunnelingState.value === 'yes'
            ? parseIntNum(tunnelingState.direction)
            : null,
        tunneling_depth_cm:
          tunnelingState.value === 'yes'
            ? parseNum(tunnelingState.depth)
            : null,
        pain: painState.present ? true : null,
        pain_level: painState.present ? parseIntNum(painState.level) : null,
        wound_care_notes: notesState.woundCareNotes.trim() || null,
        treatment_performed: notesState.treatmentPerformed.trim() || null,
        patient_response: notesState.patientResponse.trim() || null,
        additional_info: notesState.additionalInfo.trim() || null,
        not_assessed_reasons:
          Object.keys(notAssessedReasons).length > 0
            ? notAssessedReasons
            : null,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const painLevelInvalid =
    painState.present &&
    painState.level !== '' &&
    (parseInt(painState.level, 10) < 1 || parseInt(painState.level, 10) > 10);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-3xl max-h-[85vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>
            {editingAssessment ? 'Edit Assessment' : 'New Assessment'}
          </DialogTitle>
        </DialogHeader>

        <Accordion
          type='multiple'
          defaultValue={DEFAULT_SECTIONS}
          className='space-y-1'
        >
          <OverviewSection
            isAddressed={isAddressed}
            onChange={setIsAddressed}
          />
          <SizeSection
            state={sizeState}
            onChange={makePartialUpdater(setSizeState)}
          />
          <ThicknessSection
            state={thicknessState}
            onChange={makePartialUpdater(setThicknessState)}
          />
          <EdgesSection
            state={edgesState}
            onChange={makePartialUpdater(setEdgesState)}
          />
          <DrainageSection
            state={drainageState}
            onChange={makePartialUpdater(setDrainageState)}
          />
          <OdorSection
            state={odorState}
            onChange={makePartialUpdater(setOdorState)}
          />
          <WoundBedSection
            state={woundBedState}
            onChange={makePartialUpdater(setWoundBedState)}
          />
          <SurroundingTissueSection
            state={surroundingTissueState}
            onChange={makePartialUpdater(setSurroundingTissueState)}
          />
          <UnderminingSection
            state={underminingState}
            onChange={makePartialUpdater(setUnderminingState)}
          />
          <TunnelingSection
            state={tunnelingState}
            onChange={makePartialUpdater(setTunnelingState)}
          />
          <PainSection
            state={painState}
            onChange={makePartialUpdater(setPainState)}
          />
          <NotesSection
            state={notesState}
            onChange={makePartialUpdater(setNotesState)}
          />
        </Accordion>

        {saveError && (
          <p className='mt-4 text-sm text-destructive'>{saveError}</p>
        )}

        <DialogFooter className='mt-4'>
          <Button
            variant='outline'
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving || painLevelInvalid}>
            {isSaving && <Loader2 className='h-4 w-4 mr-1 animate-spin' />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WoundAssessmentDialog;
